import { useEffect, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import {
  KEYBOARD_LOCK_SUPPORTED,
  isFullscreen,
  toggleFullscreen,
} from "../input/fullscreen.js";
import { SHADOWS_ENABLED, SHADOWS_URL_FORCED } from "../render/shadows.js";
import {
  MAX_SENSITIVITY,
  MAX_VOLUME,
  MIN_SENSITIVITY,
  MIN_VOLUME,
  useSettings,
} from "../settings.js";
import { uiActions } from "./actions.js";
import { Card, CardTitle, Kbd, Modal, Note, Num, Switch, Title } from "./meta/kit.js";
import { useHud } from "./store.js";
import { TEXT, TEXT_DIM, UI_FONT, WARN } from "./theme.js";

/**
 * The three settings, drawn once and mounted twice: a TAB on the menu and an
 * overlay on Escape in a raid. Values come from `settings.ts`, not the HUD
 * store (touched only for `settingsOpen` and the screen). Sensitivity and
 * volume are live; shadows are baked at load (`render/shadows.ts`), so the
 * switch comes with a reload prompt — reloading mid-raid reconnects into the
 * same raid.
 */

const ROW_LABEL: CSSProperties = {
  flex: "0 0 130px",
  color: TEXT_DIM,
  fontSize: 12.5,
};

const NUMBER_FIELD: CSSProperties = {
  flex: "0 0 72px",
  minWidth: 0,
  textAlign: "right",
};

/** Where the slider's gold stops, as `.mm-range` reads it. */
function fillVar(value: number, min: number, max: number): CSSProperties {
  const span = max - min;
  const pct = span > 0 ? ((value - min) / span) * 100 : 0;
  return { "--mm-range-fill": `${Math.min(100, Math.max(0, pct)).toFixed(1)}%` } as CSSProperties;
}

/** The panel itself: three sections, no save button — every change is applied. */
export function SettingsPanel(): ReactElement {
  const sensitivity = useSettings((s) => s.sensitivity);
  const volume = useSettings((s) => s.volume);
  const shadows = useSettings((s) => s.shadows);
  const setSensitivity = useSettings((s) => s.setSensitivity);
  const setVolume = useSettings((s) => s.setVolume);
  const setShadows = useSettings((s) => s.setShadows);

  /**
   * What the number field holds while being typed into, or `null` when it
   * mirrors the setting. The store CLAMPS on every set, so without a draft
   * "0.5" would turn into "0.1" at the first keystroke.
   */
  const [draft, setDraft] = useState<string | null>(null);

  // Fullscreen is browser state, not a setting: read it, and re-read on change
  // so F10 pressed with the panel up flips the button's caption too.
  const [fullscreen, setFullscreen] = useState(isFullscreen);
  useEffect(() => {
    const sync = (): void => {
      setFullscreen(isFullscreen());
    };
    document.addEventListener("fullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
    };
  }, []);

  const commitSensitivity = (raw: string): void => {
    setDraft(raw);
    const n = Number(raw);
    if (raw.trim() !== "" && Number.isFinite(n)) setSensitivity(n);
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 600,
        maxHeight: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        font: UI_FONT,
        color: TEXT,
      }}
    >
      <Section title="MOUSE">
        <Row label="Sensitivity">
          <input
            type="range"
            className="mm-range"
            min={MIN_SENSITIVITY}
            max={MAX_SENSITIVITY}
            step={0.05}
            value={sensitivity}
            onChange={(ev) => {
              setDraft(null);
              setSensitivity(Number(ev.target.value));
            }}
            style={fillVar(sensitivity, MIN_SENSITIVITY, MAX_SENSITIVITY)}
          />
          <input
            type="number"
            className="metainput"
            min={MIN_SENSITIVITY}
            max={MAX_SENSITIVITY}
            step={0.05}
            value={draft ?? sensitivity}
            onChange={(ev) => {
              commitSensitivity(ev.target.value);
            }}
            onBlur={() => {
              setDraft(null);
            }}
            style={NUMBER_FIELD}
          />
        </Row>
        <Note>Counter-Strike 2 scale — 2.5 is the CS2 default.</Note>
      </Section>

      <Section title="AUDIO">
        <Row label="Master volume">
          <input
            type="range"
            className="mm-range"
            min={MIN_VOLUME}
            max={MAX_VOLUME}
            step={1}
            value={volume}
            onChange={(ev) => {
              setVolume(Number(ev.target.value));
            }}
            style={fillVar(volume, MIN_VOLUME, MAX_VOLUME)}
          />
          <Num tone={volume === 0 ? "dim" : "default"} style={NUMBER_FIELD}>
            {volume}
          </Num>
        </Row>
        <Note>50 is the reference level everything in the game is mixed for.</Note>
      </Section>

      <Section title="VIDEO">
        <Row label="Fullscreen">
          <button type="button" className="metabtn" onClick={toggleFullscreen}>
            {fullscreen ? "Leave fullscreen" : "Go fullscreen"}
          </button>
          <Kbd>F10</Kbd>
        </Row>
        <Note>
          {KEYBOARD_LOCK_SUPPORTED
            ? "In fullscreen the game also takes Ctrl+W and friends, so a stray chord cannot close the tab. Hold Esc to leave."
            : "This browser cannot hand Ctrl+W to the game even in fullscreen; you will be asked before the tab closes."}
        </Note>
        <Row label="Shadows">
          <Switch checked={shadows} onChange={setShadows} label="Real-time sun shadows" />
        </Row>
        {SHADOWS_URL_FORCED ? (
          <Note>
            Forced on for this session by <code>?shadows=1</code>. The switch above
            is still your saved preference; drop the parameter from the address
            to use it.
          </Note>
        ) : shadows === SHADOWS_ENABLED ? (
          <Note>Costs a second full scene pass. Off by default, and off is cheaper.</Note>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 2,
              color: WARN,
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              Takes effect after reload — the scene is built once, with or
              without them. Reloading mid-raid puts you back where you stood.
            </span>
            <button
              type="button"
              className="metabtn"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload now
            </button>
          </div>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Section(props: { title: string; children: ReactNode }): ReactElement {
  return (
    <Card style={{ flex: "0 0 auto", gap: 10 }}>
      <CardTitle>{props.title}</CardTitle>
      {props.children}
    </Card>
  );
}

function Row(props: { label: string; children: ReactNode }): ReactElement {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={ROW_LABEL}>{props.label}</span>
      {props.children}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The in-raid mount: the same panel over a dimmed world. Renders only when the
 * overlay is up AND we are in a raid — the second half keeps it off the menu.
 * Closing goes through `uiActions`, since the pointer has to be taken back.
 */
export function SettingsOverlay(): ReactElement | null {
  const open = useHud((s) => s.settingsOpen);
  const screen = useHud((s) => s.screen);
  if (!open || screen !== "raid") return null;

  const close = (): void => {
    uiActions().setSettingsOpen(false);
  };

  return (
    // Stacked at 0 rather than the scrim's own 65: the death overlay is drawn
    // after this one in `hud.tsx` and has to stay on top of it.
    <Modal width={620} z={0} label="Settings">
      <header style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Title size="lg">Settings</Title>
        <span style={{ flex: 1 }} />
        <Kbd>Esc</Kbd>
        <button type="button" className="metabtn" onClick={close}>
          Close
        </button>
      </header>
      <SettingsPanel />
    </Modal>
  );
}
